import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { MetaValues } from '../lib/comprasMetas'

export interface MetaDailyRow extends MetaValues {
  snapshot_date: string // YYYY-MM-DD
  source: 'auto' | 'manual'
}

interface RawRow {
  snapshot_date: string
  consignado: number | null
  ruptura: number | null
  encalhe: number | null
  otd: number | null
  crm: number | null
  degustacoes: number | null
  source: 'auto' | 'manual'
}

/** Histórico diário das Metas de Compras (tabela compras_metas_daily).
 *  Leitura por data + ações de gravação (snapshot de hoje, backfill manual, excluir). */
export function useComprasMetasHistory() {
  const [byDate, setByDate] = useState<Record<string, MetaDailyRow>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('compras_metas_daily')
      .select('snapshot_date,consignado,ruptura,encalhe,otd,crm,degustacoes,source')
      .order('snapshot_date', { ascending: false })
    if (error) {
      setError(error.message)
    } else {
      const map: Record<string, MetaDailyRow> = {}
      for (const r of (data ?? []) as RawRow[]) {
        map[r.snapshot_date] = {
          snapshot_date: r.snapshot_date,
          consignado: r.consignado, ruptura: r.ruptura, encalhe: r.encalhe,
          otd: r.otd, crm: r.crm, degustacoes: r.degustacoes, source: r.source,
        }
      }
      setByDate(map)
      setError(null)
    }
    setLoading(false)
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  /** Calcula e grava os 4 índices automáticos de hoje (via RPC) + valores manuais opcionais. */
  const saveToday = useCallback(async (crm: number | null, degustacoes: number | null) => {
    const { error } = await supabase.rpc('fn_compras_metas_snapshot_today', {
      p_crm: crm, p_degustacoes: degustacoes,
    })
    if (error) { setError(error.message); return false }
    await fetchAll()
    return true
  }, [fetchAll])

  /** Backfill/edição manual de uma data (só os campos informados são gravados). */
  const upsertManual = useCallback(async (date: string, v: Partial<MetaValues>) => {
    const { error } = await supabase.rpc('fn_compras_metas_upsert_manual', {
      p_date: date,
      p_consignado: v.consignado ?? null,
      p_ruptura: v.ruptura ?? null,
      p_encalhe: v.encalhe ?? null,
      p_otd: v.otd ?? null,
      p_crm: v.crm ?? null,
      p_degustacoes: v.degustacoes ?? null,
    })
    if (error) { setError(error.message); return false }
    await fetchAll()
    return true
  }, [fetchAll])

  const removeDate = useCallback(async (date: string) => {
    const { error } = await supabase.from('compras_metas_daily').delete().eq('snapshot_date', date)
    if (error) { setError(error.message); return false }
    await fetchAll()
    return true
  }, [fetchAll])

  return { byDate, loading, error, saveToday, upsertManual, removeDate, refetch: fetchAll }
}
